import { lookupSecurities } from '../../services/apis/securities'
import { SearchSecurityResponse_SecuritySearchItem, Security, SecurityCategory_CategoryCode, securityCategory_CategoryCodeToJSON, SecurityExpression } from '../../proto/generated/security'
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import InfiniteScroll from 'react-infinite-scroll-component'
import './SecuritySearchModal.css'
import { SEARCH_SCROLL_CONTAINER_ID } from './SecuritySearchModal'
import { getSecurityKey, getSecuritySymbol } from '../../util/security'
import type { InstrumentLabels } from '../../util/cashInstrument'
import { cleanUpBadParenthesis, convertExpressionToDisplayExpression } from '../../util/expression'
import SecurityRow from './SecurityRow'
import CompareSecurityRow from './CompareSecurityRow'
import type { CompareSecurityPlacement } from './typings'
import SearchNoResults from './SearchNoResults'
import { Exchange } from '../../proto/generated/common'
import { checkSearchTokensPresence } from '../../util/general'
import { getStrategyIconFromText } from '../../util/strategyBuilder'
import { useSelector } from 'react-redux'
import { selectActiveWidget } from '../../selectors/widgetDashboard'
import { selectActiveLayoutId } from '../../selectors/home'
import { simulatorSecurities } from '../../assets/constants'
import { isFoUnderlyingSearchContext } from '../../util/foUnderlyingSearch'

interface SecurityInfiniteScrollProps {
    categoryCode: number
    instrument?: string
    searchText: string
    /** Exchange typed as a prefix (`BSE:`): that listing leads, the other exchange is grouped under it. */
    exchange?: string
    /** instrument -> display name from the categories response. */
    instrumentLabels?: InstrumentLabels
    expressions: SecurityExpression[]
    onExpressionDeleted: any
    cachedSecurities: RefObject<Security[]>
    nameToExpression: RefObject<Map<string, string>>
    expressionToName: RefObject<Map<string, string>>
    cachedStrategyExpressions: RefObject<SecurityExpression[]>
    highlightSelected: boolean
    onSecuritySelect?: any
    selectedItems?: Set<string>
    tailIcon?: boolean
    deleteIcon?: boolean
    focusedIndex: number
    expressionMode: boolean
    onMaxFocusableChange: (max: number) => void
    /** Preview text in the search box (used while arrowing through rows). */
    setInputValue: (value: string) => void
    showOnlySimulatorSecurities: boolean
    showOnlyFOSecurities?: boolean
    watchlistMode?: boolean
    compareSecurityPicker?: boolean
    onCompareSecurityPick?: (security: Security, placement: CompareSecurityPlacement) => void
    comparePickerSubmitRef?: RefObject<(() => void) | null>
    /** Offered on a miss while a category filter is active; resets the search to every category. */
    onSearchAllCategories?: () => void
}

const SecurityInfiniteScroll = ({
    categoryCode,
    instrument,
    searchText,
    exchange,
    instrumentLabels,
    expressions: userExpressions,
    cachedSecurities,
    cachedStrategyExpressions,
    onSecuritySelect,
    onExpressionDeleted,
    highlightSelected,
    tailIcon,
    deleteIcon,
    expressionMode,
    setInputValue,
    onMaxFocusableChange,
    expressionToName,
    nameToExpression,
    focusedIndex,
    selectedItems = new Set(),
    showOnlySimulatorSecurities,
    showOnlyFOSecurities,
    watchlistMode,
    compareSecurityPicker,
    onCompareSecurityPick,
    comparePickerSubmitRef,
    onSearchAllCategories
}: SecurityInfiniteScrollProps) => {
    const pageSize = 20
    const pageNumber = useRef<number>(0)
    const previousPageStrategyExpressions = useRef<SecurityExpression[]>([])
    const previousPageSecurities = useRef<Security[]>([])
    const [totalNumberOfItems, setTotalNumberOfItems] = useState<number>(1000)
    const totalItemsRef = useRef<number>(1000)
    const [securities, setSecurities] = useState<Security[]>(categoryCode === SecurityCategory_CategoryCode.SPREADS ? [] : cachedSecurities.current)
    const [nothingMatched, setNothingMatched] = useState<boolean>(false)
    /** Related securities per result row (other exchange listing, near / rolling future, ATM CE-PE), keyed by EXCHANGE:CODE. */
    const relatedByKey = useRef<Map<string, Security[]>>(new Map())
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
    const selectedSecurities = Array.from(selectedItems)
    const [strategySearch, setStrategySearch] = useState<boolean>(false)
    const [strategyExpressions, setStrategyExpressions] = useState<SecurityExpression[]>(
        [SecurityCategory_CategoryCode.SPREADS, SecurityCategory_CategoryCode.ALL].includes(categoryCode) ? cachedStrategyExpressions.current : []
    )
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId))

    const fetchSecurities = useCallback(
        (categoryCode: number, searchText: string, pageNumber: number, pageSize: number) => {
            if (showOnlySimulatorSecurities) {
                previousPageStrategyExpressions.current = []
                previousPageSecurities.current = simulatorSecurities
                setStrategyExpressions(previousPageStrategyExpressions.current)
                setSecurities(previousPageSecurities.current)
            } else {
                lookupSecurities({
                    searchText: searchText,
                    categoryCode: securityCategory_CategoryCodeToJSON(categoryCode),
                    pageNumber: pageNumber,
                    pageSize: pageSize,
                    ...(instrument ? { instrument } : {}),
                    ...(exchange ? { exchange } : {}),
                    hasFo: isFoUnderlyingSearchContext({
                        showOnlyFOSecurities,
                        watchlistMode,
                        widgetType: activeWidget?.widgetType,
                    })
                        ? true
                        : false,
                })
                    .then((response) => {
                        const responseExpressions = response.securitySearchItems
                            .filter((item: SearchSecurityResponse_SecuritySearchItem) => item.isExpression && item.expression)
                            .map((item) => item.expression!)
                        const securityItems = response.securitySearchItems.filter(
                            (item: SearchSecurityResponse_SecuritySearchItem) => !item.isExpression && item.securities.length
                        )
                        const responseSecurities = securityItems.map((item) => item.securities[0])
                        if (pageNumber === 0) {
                            relatedByKey.current = new Map()
                            setExpandedKeys(new Set())
                        }
                        securityItems.forEach((item) => {
                            if (item.related?.length) {
                                relatedByKey.current.set(getSecurityKey(item.securities[0].exchange, item.securities[0].code), item.related as unknown as Security[])
                            }
                        })
                        previousPageStrategyExpressions.current = pageNumber === 0 ? responseExpressions : previousPageStrategyExpressions.current.concat(responseExpressions || [])
                        previousPageSecurities.current = pageNumber === 0 ? responseSecurities : previousPageSecurities.current.concat(responseSecurities || [])
                        if (response.totalCount !== totalItemsRef.current) {
                            totalItemsRef.current = response.totalCount
                            setTotalNumberOfItems(response.totalCount)
                        }
                        if (pageNumber === 0) {
                            cachedSecurities.current = responseSecurities
                            cachedStrategyExpressions.current = responseExpressions
                            if (response.securitySearchItems.length && response.securitySearchItems[0].isExpression) {
                                setStrategySearch(true)
                            } else {
                                setStrategySearch(false)
                            }
                        }
                        setNothingMatched(pageNumber === 0 && (!responseSecurities || responseSecurities.length === 0) && (!responseExpressions || responseExpressions.length === 0))

                        setStrategyExpressions(previousPageStrategyExpressions.current)
                        setSecurities(previousPageSecurities.current)
                    })
                    .catch((e) => {
                        console.error(e)
                    })
            }
        },
        [activeWidget?.widgetType, cachedSecurities, cachedStrategyExpressions, exchange, instrument, showOnlyFOSecurities, showOnlySimulatorSecurities, watchlistMode]
    )

    const fetchNextPage = useCallback(() => {
        pageNumber.current += 1
        fetchSecurities(categoryCode, searchText, pageNumber.current, pageSize)
    }, [categoryCode, fetchSecurities, searchText])

    // Category / type changes: clear, scroll back to the top and refetch. The scroll container keeps
    // its old scrollTop otherwise, which leaves the paging trigger already "past" the new, shorter list.
    useEffect(() => {
        pageNumber.current = 0
        previousPageSecurities.current = []
        previousPageStrategyExpressions.current = []
        setSecurities([])
        setStrategyExpressions([])
        setNothingMatched(false)
        document.getElementById(SEARCH_SCROLL_CONTAINER_ID)?.scrollTo({ top: 0 })
        fetchSecurities(categoryCode, searchText, 0, pageSize)
        // searchText intentionally omitted — debounced below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryCode, instrument, fetchSecurities])

    // Typing: debounce refetch so the list does not thrash on every keystroke.
    // Skip the initial mount — category/instrument effect already loads the first page.
    const skipSearchDebounceOnce = useRef(true)
    useEffect(() => {
        if (skipSearchDebounceOnce.current) {
            skipSearchDebounceOnce.current = false
            return
        }
        const timeoutId = setTimeout(() => {
            pageNumber.current = 0
            fetchSecurities(categoryCode, searchText, 0, pageSize)
        }, 200)
        return () => {
            if (timeoutId) clearTimeout(timeoutId)
        }
        // categoryCode/instrument handled above
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, fetchSecurities])

    useEffect(() => {
        strategyExpressions.forEach((exp) => {
            const displayExpression = cleanUpBadParenthesis(convertExpressionToDisplayExpression(exp.expression, exp.securities))
            expressionToName.current.set(displayExpression, exp.name)
            nameToExpression.current.set(exp.name, displayExpression)
        })
    }, [strategyExpressions])

    const filteredUserExpressions = useMemo(() => {
        return userExpressions.filter(
            (item) =>
                (categoryCode === SecurityCategory_CategoryCode.ALL || categoryCode === SecurityCategory_CategoryCode.SPREADS) &&
                (checkSearchTokensPresence(searchText.trim().split(' '), item.expression.toLowerCase()) || checkSearchTokensPresence(searchText.trim().split(' '), item.name.toLowerCase()))
        )
    }, [userExpressions, categoryCode, searchText])

    const strategyExpressionsLength = useMemo(() => strategyExpressions.length, [strategyExpressions])
    const securitiesLength = useMemo(() => securities.length, [securities])
    const filteredUserExpressionsLength = useMemo(() => filteredUserExpressions.length, [filteredUserExpressions])

    useEffect(() => {
        onMaxFocusableChange(filteredUserExpressionsLength + securitiesLength + strategyExpressionsLength)
    }, [filteredUserExpressionsLength, securitiesLength, strategyExpressionsLength])

    useEffect(() => {
        if (!comparePickerSubmitRef || !compareSecurityPicker || !onCompareSecurityPick) {
            if (comparePickerSubmitRef) comparePickerSubmitRef.current = null
            return
        }
        comparePickerSubmitRef.current = () => {
            let secIndex = -1
            if (strategySearch) {
                const off = filteredUserExpressionsLength + strategyExpressionsLength
                if (focusedIndex >= off) secIndex = focusedIndex - off
            } else if (focusedIndex < securitiesLength) {
                secIndex = focusedIndex
            }
            if (secIndex < 0 || secIndex >= securitiesLength) return
            onCompareSecurityPick(securities[secIndex], 'same_percentage_scale')
        }
        return () => {
            comparePickerSubmitRef.current = null
        }
    }, [
        comparePickerSubmitRef,
        compareSecurityPicker,
        onCompareSecurityPick,
        focusedIndex,
        strategySearch,
        filteredUserExpressionsLength,
        strategyExpressionsLength,
        securitiesLength,
        securities
    ])

    const filteredUserExpressionsView = useMemo(() => {
        return filteredUserExpressions.map((expression, index) => {
            const focused = index + (strategySearch ? 0 : securities.length) === focusedIndex
            return (
                <SecurityRow
                    exchange={expression.securities[0]?.exchange ?? 0}
                    symbol={expression.name}
                    colorSymbol={expression.securities.reduce((acc, curr) => acc + `${curr.symbol ?? ''} `, '')}
                    displayName={String(convertExpressionToDisplayExpression(expression.expression, expression.securities) ?? '')}
                    selected={selectedSecurities.find((sec) => sec === expression.expression) !== undefined}
                    categoryCode={SecurityCategory_CategoryCode.SPREADS}
                    key={`${expression.id}-${index}`}
                    highlightSelected={highlightSelected}
                    searchTerm={searchText}
                    isExpression={true}
                    focused={focused}
                    expressionMode={expressionMode}
                    onSecuritySelect={(selected: boolean, focused: boolean) => {
                        if (focused) {
                            const displayExp = cleanUpBadParenthesis(convertExpressionToDisplayExpression(expression.expression, expression.securities))
                            setInputValue(expressionToName.current.get(displayExp) ?? displayExp)
                        } else {
                            onSecuritySelect(expression, selected, focused, 'EXPRESSION')
                        }
                    }}
                    onSecurityDeleted={() => onExpressionDeleted(expression.expression)}
                    tailIcon={tailIcon}
                    deleteIcon={deleteIcon}
                />
            )
        })
    }, [
        filteredUserExpressions,
        strategySearch,
        securities.length,
        focusedIndex,
        selectedSecurities,
        highlightSelected,
        searchText,
        tailIcon,
        deleteIcon,
        setInputValue,
        expressionToName,
        onSecuritySelect,
        onExpressionDeleted,
        expressionMode
    ])

    const strategyExpressionsView = useMemo(() => {
        return strategyExpressions.map((expression, index) => {
            const focused = index + filteredUserExpressions.length + (strategySearch ? 0 : securities.length) === focusedIndex
            return (
                <SecurityRow
                    exchange={expression.securities[0].exchange}
                    symbol={expression.name}
                    colorSymbol={expression.securities.reduce((acc, curr) => acc + `${curr.symbol ?? ''} `, '')}
                    displayName={String(convertExpressionToDisplayExpression(expression.expression, expression.securities) ?? '')}
                    selected={selectedSecurities.find((sec) => sec === expression.expression) !== undefined}
                    categoryCode={SecurityCategory_CategoryCode.SPREADS}
                    key={`${expression.expression}-${index}`}
                    searchTerm={searchText}
                    isExpression={true}
                    focused={focused}
                    expressionMode={expressionMode}
                    highlightSelected={highlightSelected}
                    logo={getStrategyIconFromText(expression.name)}
                    onSecuritySelect={(selected: boolean, focused: boolean) => {
                        if (focused) {
                            const displayExp = cleanUpBadParenthesis(convertExpressionToDisplayExpression(expression.expression, expression.securities))
                            setInputValue(expressionToName.current.get(displayExp) ?? displayExp)
                        } else {
                            onSecuritySelect(expression, selected, focused, 'STRATEGY_EXPRESSION')
                        }
                    }}
                    tailIcon={tailIcon}
                    deleteIcon={undefined}
                />
            )
        })
    }, [
        strategyExpressions,
        filteredUserExpressions.length,
        strategySearch,
        securities.length,
        focusedIndex,
        selectedSecurities,
        searchText,
        highlightSelected,
        tailIcon,
        setInputValue,
        expressionToName,
        onSecuritySelect,
        expressionMode
    ])

    return (
        <>
            {nothingMatched ? (
                <SearchNoResults query={searchText.trim()} onSearchAllCategories={onSearchAllCategories} />
            ) : (
                <InfiniteScroll
                    // remount per category / type so the component's "page already requested" state resets with the list
                    key={`${categoryCode}-${instrument ?? ''}`}
                    dataLength={filteredUserExpressionsLength + strategyExpressionsLength + securitiesLength}
                    next={fetchNextPage}
                    scrollableTarget={SEARCH_SCROLL_CONTAINER_ID}
                    hasMore={strategyExpressionsLength + securitiesLength < totalNumberOfItems}
                    loader={totalNumberOfItems > strategyExpressionsLength + securitiesLength && <Typography sx={{ textAlign: 'center' }}></Typography>}
                >
                    {strategySearch && filteredUserExpressionsView}
                    {strategySearch && strategyExpressionsView}
                    {securities.map((security, index) => {
                        const focused = index + (strategySearch ? filteredUserExpressions.length + strategyExpressions.length : 0) === focusedIndex
                        const securityKey = getSecurityKey(security.exchange, security.code)
                        const related = relatedByKey.current.get(securityKey) ?? []
                        const expanded = expandedKeys.has(securityKey)
                        // collapsed: show every exchange the company is listed on (its own first); expanded: just its own
                        const listingExchanges = expanded
                            ? [security.exchange]
                            : [security.exchange, ...related.filter((r) => r.categoryCode === security.categoryCode).map((r) => r.exchange)]
                        if (compareSecurityPicker && onCompareSecurityPick) {
                            return (
                                <CompareSecurityRow
                                    key={`${security.code}-${index}`}
                                    security={security}
                                    instrumentLabels={instrumentLabels}
                                    exchanges={listingExchanges}
                                    rolling={(security as { rolling?: boolean }).rolling}
                                    searchTerm={searchText}
                                    focused={focused}
                                    onPick={(placement) => onCompareSecurityPick(security, placement)}
                                />
                            )
                        }
                        return (
                            <React.Fragment key={`${securityKey}-${index}`}>
                                <SecurityRow
                                    series={security.series}
                                    exchange={security.exchange}
                                    symbol={security.symbol || ''}
                                    displayName={security.displayName || ''}
                                    categoryCode={security.categoryCode || SecurityCategory_CategoryCode.CATEGORY_CODE_UNKNOWN}
                                    instrument={security.instrument}
                                    instrumentLabels={instrumentLabels}
                                    rolling={(security as { rolling?: boolean }).rolling}
                                    logoUrl={security.logoUrl}
                                    onSecuritySelect={(selected: boolean, focused: boolean) => {
                                        if (focused && !expressionMode) {
                                            setInputValue(getSecuritySymbol(security.exchange ?? Exchange.NSE, security.symbol || ''))
                                        } else if (!focused || expressionMode) {
                                            onSecuritySelect(security, selected, focused, 'SECURITY')
                                        }
                                    }}
                                    focused={focused}
                                    expressionMode={expressionMode}
                                    highlightSelected={highlightSelected}
                                    selected={selectedSecurities.find((sec) => sec === securityKey) !== undefined}
                                    searchTerm={searchText}
                                    tailIcon={tailIcon}
                                    deleteIcon={deleteIcon}
                                    expandable={related.length > 0}
                                    expanded={expanded}
                                    exchanges={listingExchanges}
                                    onToggleExpand={() =>
                                        setExpandedKeys((prev) => {
                                            const next = new Set(prev)
                                            if (next.has(securityKey)) next.delete(securityKey)
                                            else next.add(securityKey)
                                            return next
                                        })
                                    }
                                />
                                {expanded &&
                                    related.map((rel, relIndex) => {
                                        const relKey = getSecurityKey(rel.exchange, rel.code)
                                        return (
                                            <SecurityRow
                                                key={`${relKey}-${relIndex}`}
                                                indent
                                                series={rel.series}
                                                exchange={rel.exchange}
                                                symbol={rel.symbol || ''}
                                                displayName={rel.displayName || ''}
                                                categoryCode={rel.categoryCode || SecurityCategory_CategoryCode.CATEGORY_CODE_UNKNOWN}
                                                instrument={rel.instrument}
                                                instrumentLabels={instrumentLabels}
                                                rolling={(rel as { rolling?: boolean }).rolling}
                                                onSecuritySelect={(selected: boolean, focused: boolean) => {
                                                    if (!focused || expressionMode) {
                                                        onSecuritySelect(rel, selected, focused, 'SECURITY')
                                                    }
                                                }}
                                                focused={false}
                                                expressionMode={expressionMode}
                                                highlightSelected={highlightSelected}
                                                selected={selectedSecurities.find((sec) => sec === relKey) !== undefined}
                                                searchTerm={searchText}
                                                tailIcon={tailIcon}
                                                deleteIcon={deleteIcon}
                                            />
                                        )
                                    })}
                            </React.Fragment>
                        )
                    })}
                    {!strategySearch && filteredUserExpressionsView}
                    {!strategySearch && strategyExpressionsView}
                </InfiniteScroll>
            )}
        </>
    )
}

export default SecurityInfiniteScroll
